import React, { Fragment, useContext, useRef } from 'react';
import { Form, Row, Col, Select, Input } from 'antd';
import MobileInput_v2 from './ReusableMobileInput';
import ReusableInput from '../../stories/components/ReusableInput';
import Select_v2 from '../../stories/components/Select_v2';
import SearchAddress_v2 from '../../stories/components/SearchAddress_v2';
import MaterialTimePicker from '../orders/MaterialTimePicker';
import { formatAddress } from '../../common/Common';
import { SideBySideFormItem,Radio } from '../../common/UIComponents';
import AppConfig from '../../config/AppConfig';
import PropTypes from 'prop-types';
import Checkbox_v2 from '../../stories/components/Checkbox_v2';
import { CommercialIcon, ResidentialIcon } from '../../common/AddressTypeIcons';
import { YardIcon, DepotIcon, PortIcon, OtherLocationIcon } from './DrayageIcons';
import { OrgContext } from '../../context/OrgContext';
import moment from 'moment';
import "./AddressForm_v2.scss";

const parseOperationalTime = (timeValue) => {
  if (!timeValue) return null;
  if (moment.isMoment(timeValue)) return timeValue;

  const isoParsed = moment(timeValue, moment.ISO_8601, true);
  if (isoParsed.isValid()) return isoParsed;

  const parsed = moment(timeValue, ["HH:mm", "H:mm", "hh:mm A", "h:mm A"], true);
  return parsed.isValid() ? parsed : null;
};

// Operating hours are stored as a plain "HH:mm" time-of-day string (BE consumers
// do `start_time.split(':')` and default to '08:00'/'20:00'). Storing a full ISO
// datetime broke those and shifted the time to UTC.
const normalizeOperationalTime = (timeValue) => {
  if (!timeValue) return "";
  const m = moment.isMoment(timeValue) ? timeValue : moment(timeValue);
  return m.isValid() ? m.format("HH:mm") : "";
};

const VALID_SECONDARY_LOC_TYPES = ["YARD", "DEPOT", "PORT", "OTHERS"];
const normalizeSecondaryLocType = (value) => {
  const normalized = (value || "").toUpperCase();
  return VALID_SECONDARY_LOC_TYPES.includes(normalized) ? normalized : "OTHERS";
};

export const AddAddressForm = ({
  location,
  onAddressSelect,
  handleOnChangeDirect,
  typeOfOrder,
  handleOnChangeAddress,
  showSearch = false,
  getAvailableDates = () => { },
  verifyZipcode = () => { },
  isCrossDock = false,
  hideWebResults = false,
  hidePredefined = false,
  linehaul_code = false,
  linehaul_label = false,
  disableAddressFields = false,
  hideSaveAsPredefined = false,
  showPredefinedFields = false,
  showNameCode = false,
  showOperationalUse = false,
  showOperationalHours = false,
  fromPredefinedStopsPage = false,
  isMilitaryTime = false,
  organizationSettings = {},
  onSelectForCrossDock=()=>{},
  verifyWhZipCodes=()=>{},
  tempLocation = null,
  orderTypeConfig = {},
  selectedWarehouseId,
  accountConfig={}
}) => {
  const operationalUseOptions = [
    { value: "FINAL_MILE", label: "Cartage/White Glove" },
    { value: "DRAYAGE", label: "Drayage" },
    { value: "BOTH", label: "Both" },
  ];
  const secondaryLocationTypeOptions = [
    {
      value: "YARD",
      label: "Yard",
      icon: <YardIcon width={16} height={16} />,
    },
    {
      value: "DEPOT",
      label: "Depot",
      icon: <DepotIcon width={16} height={16} />,
    },
    {
      value: "PORT",
      label: "Port",
      icon: <PortIcon width={16} height={16} />,
    },
    {
      value: "OTHERS",
      label: "Misc",
      icon: <OtherLocationIcon width={16} height={16} />,
    },
  ];
  const selectedOperationalUse = location?.operational_use || "FINAL_MILE";
  const selectedSecondaryLocType = normalizeSecondaryLocType(location?.secondary_loc_type);
  const { orgSettings: contextOrgSettings = {} } = useContext(OrgContext);
  const resolvedOrgSettings =
    organizationSettings && Object.keys(organizationSettings).length > 0
      ? organizationSettings
      : contextOrgSettings;
  const isPhone1Required = fromPredefinedStopsPage || !!location?.is_predefined;
  const accountDefaultLocationType = accountConfig?.default_location_type;

  return (
    !isCrossDock ?
    <Fragment>
      <Row gutter={[0, 8]} className="address-form-v2" data-testid="address-form">

        {/* ===== SECTION 1: CONTACT INFORMATION ===== */}
        <div className="address-form-section-title">Contact Information</div>

        <Row gutter={8}>
          {/* Company Name */}
          <Col span={8}>
            <Form.Item colon={false} label="Company Name" className={`custom-form ${contextOrgSettings?.company_name_mandatory == "true" ? "required-star-right" : ""}`}>
              <ReusableInput
                value={location?.company_name}
                onChange={(e) =>
                  handleOnChangeDirect("company_name", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>

          {/* First Name */}
          <Col span={8}>
            <Form.Item colon={false} label="First Name" className="custom-form">
              <ReusableInput
                value={location?.customer_first_name ?? location?.first_name ?? ""}
                onChange={(e) =>
                  handleOnChangeDirect("customer_first_name", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>

          {/* Last Name */}
          <Col span={8}>
            <Form.Item colon={false} label="Last Name" className="custom-form">
              <ReusableInput
                value={location?.customer_last_name ?? location?.last_name ?? ""}
                onChange={(e) =>
                  handleOnChangeDirect("customer_last_name", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={8}>
          {/* Email Address - Full Width */}
          <Col span={24}>
            <Form.Item colon={false} label="Email Address" className="custom-form">
              <ReusableInput
                data-testid="company-name-input"
                type="email"
                value={location?.customer_email}
                onChange={(e) =>
                  handleOnChangeDirect("customer_email", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={8}>
          {/* Phone 1 */}
          <Col span={12} data-testid="customer-phone1">
            <Form.Item
              colon={false}
              label="Phone 1"
              className={`custom-form ${isPhone1Required ? "required-star-right" : ""}`}
            >
              <MobileInput_v2
              
                value={location?.customer_phone_one}
                handleInputChange={(val) =>
                  handleOnChangeDirect("customer_phone_one", val)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>

          {/* Phone 2 */}
          <Col span={12} data-testid="customer-phone2">
            <Form.Item colon={false} label="Phone 2" className="custom-form">
              <MobileInput_v2
                value={location?.customer_phone_two}
                handleInputChange={(val) =>
                  handleOnChangeDirect("customer_phone_two", val)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== SECTION 2: ADDRESS ===== */}
        <div className="address-form-section-title">Address</div>

        {/* Address Type, Operational Use, Drayage Category - all in one row */}
        <Row gutter={8}>
          <Col span={8}>
            <Form.Item
              colon={false}
              label="Address Type"
              className="custom-form required-star-right"
            >
                <Select
                style={{ width: "100%" }}
                value={location?.address_type || (accountDefaultLocationType || "COMMERCIAL")}
                defaultValue={accountDefaultLocationType || "COMMERCIAL"}
                onChange={(value) => handleOnChangeDirect("address_type", value)}
                disabled={disableAddressFields || false}
              >
                <Select.Option value="RESEDENTIAL">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                    <ResidentialIcon />
                    <span>Residential</span>
                  </span>
                </Select.Option>
                <Select.Option value="COMMERCIAL">
                  <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                    <CommercialIcon />
                    <span>Commercial</span>
                  </span>
                </Select.Option>
              </Select>
            </Form.Item>
          </Col>

          {/* Operational Use - only show when fromPredefinedStopsPage is true */}
          {fromPredefinedStopsPage &&
            showOperationalUse &&
            resolvedOrgSettings?.drayage_enabled === "true" && (
            <Col span={8}>
              <Form.Item
                colon={false}
                label="Operational Use"
                className="custom-form required-star-right"
              >
                <Select
                  style={{ width: "100%" }}
                  value={selectedOperationalUse}
                  defaultValue="FINAL_MILE"
                  dropdownMatchSelectWidth={false}
                  dropdownStyle={{ minWidth: 220 }}
                  onChange={(value) => {
                    handleOnChangeDirect("operational_use", value);
                    if (value === "FINAL_MILE") {
                      handleOnChangeDirect("secondary_loc_type", "OTHERS");
                    }
                  }}
                  disabled={disableAddressFields || false}
                >
                  {operationalUseOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            )}

          {/* Drayage Category - show when fromPredefinedStopsPage is true AND operational use is not FINAL_MILE */}
          {fromPredefinedStopsPage && showPredefinedFields && selectedOperationalUse !== "FINAL_MILE" && (
            <Col span={8}>
              <Form.Item
                colon={false}
                label="Drayage Category"
                className="custom-form required-star-right"
              >
                <Select
                  style={{ width: "100%" }}
                  value={selectedSecondaryLocType}
                  defaultValue="OTHERS"
                  onChange={(value) =>
                    handleOnChangeDirect("secondary_loc_type", value)
                  }
                  disabled={disableAddressFields || false}
                >
                  {secondaryLocationTypeOptions.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                        {option.icon}
                        <span>{option.label}</span>
                      </span>
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          )}
        </Row>

        {/* Address Search (when editing) */}
        {showSearch && (
          <Row gutter={8}>
            <Col span={24}>
              <Form.Item
                colon={false}
                label="Address Search"
                className="custom-form"
              >
                <SearchAddress_v2
                  onSelect={onAddressSelect}
                  doesAddressExist={true}
                  linehaul_code={linehaul_code}
                  linehaul_label={linehaul_label}
                  hideWebResults={hideWebResults}
                  hidePredefined={hidePredefined}
                  orderTypeConfig={orderTypeConfig}
                  selectedWarehouseId={selectedWarehouseId}
                />
              </Form.Item>
            </Col>
          </Row>
        )}

        {/* Address Line 1 */}
        <Row gutter={8}>
          <Col span={24}>
            <Form.Item
              colon={false}
              label="Address Line 1"
              className="custom-form required-star-right"
            >
              <ReusableInput
                value={location?.customer_address_line_1}
                onChange={(e) =>
                  handleOnChangeDirect("customer_address_line_1", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* Address Line 2 */}
        <Row gutter={8}>
          <Col span={24}>
            <Form.Item
              colon={false}
              label="Address Line 2"
              className="custom-form"
            >
              <ReusableInput
                value={location?.customer_address_line_2}
                onChange={(e) =>
                  handleOnChangeDirect("customer_address_line_2", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>
        </Row>

        {/* City, State, Zip, Country */}
        <Row gutter={8}>
          <Col span={6}>
            <Form.Item colon={false} label="City" className="custom-form required-star-right">
              <ReusableInput
                value={location?.customer_city}
                onChange={(e) =>
                  handleOnChangeDirect("customer_city", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              colon={false}
              label="State"
              className="custom-form required-star-right"
            >
              <ReusableInput
                value={location?.customer_state}
                onChange={(e) =>
                  handleOnChangeDirect("customer_state", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              colon={false}
              label="Zip"
              className="custom-form required-star-right"
            >
              <ReusableInput
                value={location?.customer_zipcode}
                onKeyPress={(e) => {
                  if (!/^\d$/.test(e.key)) {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const sanitized = String(e.target.value || "").replace(/\D/g, "");
                  handleOnChangeDirect("customer_zipcode", sanitized);
                //   if(e.target.value.length >= 5){
                //   verifyWhZipCodes(tempLocation?.customer_zipcode, location?.type_of_loc);
                // }
                }}
                onBlur={() => {
                  getAvailableDates(location?.customer_zipcode);
                  verifyZipcode(location?.customer_zipcode, location?.type_of_loc);
                  // verifyWhZipCodes(tempLocation?.customer_zipcode, location?.type_of_loc)
                }}
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item
              colon={false}
              label="Country"
              className="custom-form required-star-right"
            >
              <ReusableInput
                value={location?.customer_country}
                onChange={(e) =>
                  handleOnChangeDirect("customer_country", e.target.value)
                }
                disabled={disableAddressFields || false}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item
              colon={false}
              label="Notes"
              className="custom-form notes-form-item"
            >
              <Input.TextArea
                value={location?.notes || ""}
                onChange={(e) =>
                  handleOnChangeDirect("notes", e.target.value)
                }
                disabled={disableAddressFields || false}
                rows={2}
                placeholder="Enter location notes..."
              />
            </Form.Item>
          </Col>
        </Row>

        {/* ===== SECTION 3: SAVE AS PREDEFINED CHECKBOX ===== */}
        {!hideSaveAsPredefined && (
          <Row gutter={8} style={{ marginTop: "16px" }}>
            <Col span={24}>
              <Checkbox_v2
                checkValue={location?.is_predefined}
                handleChange={(val) => {
                  handleOnChangeDirect("is_predefined", val);
                  if (val) {
                    handleOnChangeDirect("location_name", "");
                    handleOnChangeDirect("location_code", "");
                  }
                }}
                label={"Save as Predefined Location"}
                disabled={disableAddressFields || false}
              />
            </Col>
          </Row>
        )}

        {/* ===== SECTION 4: PREDEFINED LOCATION FIELDS (Conditional) ===== */}
        {(location?.is_predefined || showPredefinedFields || showNameCode || showOperationalHours) && (
          <Fragment>
            {/* Name and Code - only show when showNameCode is true */}
            {(location?.is_predefined || showNameCode) && (
              <Row gutter={8}>
                <Col span={12}>
                  <Form.Item
                    colon={false}
                    label="Name"
                    className="custom-form required-star-right"
                  >
                    <ReusableInput
                      value={location?.location_name || ""}
                      onChange={(e) =>
                        handleOnChangeDirect(
                          "location_name",
                          e.target.value
                        )
                      }
                      disabled={disableAddressFields || false}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    colon={false}
                    label="Code"
                    className="custom-form required-star-right"
                  >
                    <ReusableInput
                      value={location?.location_code || ""}
                      onChange={(e) =>
                        handleOnChangeDirect(
                          "location_code",
                          e.target.value?.toUpperCase()
                        )
                      }
                      disabled={disableAddressFields || false}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {/* Operational Use and Drayage Category - show after Name/Code when is_predefined is true AND not from predefined stops page */}
            {!fromPredefinedStopsPage &&
              location?.is_predefined &&
              resolvedOrgSettings?.drayage_enabled === "true" && (
              <Row gutter={8} style={{ marginTop: "8px" }}>
                <Col span={12}>
                  <Form.Item
                    colon={false}
                    label="Operational Use"
                    className="custom-form required-star-right"
                  >
                    <Select
                      style={{ width: "100%" }}
                      value={selectedOperationalUse}
                      defaultValue="FINAL_MILE"
                      dropdownMatchSelectWidth={false}
                      dropdownStyle={{ minWidth: 220 }}
                      onChange={(value) => {
                        handleOnChangeDirect("operational_use", value);
                        if (value === "FINAL_MILE") {
                          handleOnChangeDirect("secondary_loc_type", "OTHERS");
                        }
                      }}
                      disabled={disableAddressFields || false}
                    >
                      {operationalUseOptions.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                {selectedOperationalUse !== "FINAL_MILE" && (
                  <Col span={12}>
                    <Form.Item
                      colon={false}
                      label="Drayage Category"
                      className="custom-form required-star-right"
                    >
                      <Select
                        style={{ width: "100%" }}
                        value={selectedSecondaryLocType}
                        defaultValue="OTHERS"
                        onChange={(value) =>
                          handleOnChangeDirect("secondary_loc_type", value)
                        }
                        disabled={disableAddressFields || false}
                      >
                        {secondaryLocationTypeOptions.map((option) => (
                          <Select.Option key={option.value} value={option.value}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
                              {option.icon}
                              <span>{option.label}</span>
                            </span>
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}
              </Row>
              )}

            {/* Operational Hours - only show when showOperationalHours is true */}
            {(location?.is_predefined || showOperationalHours) && (
              <Fragment>
                <div className="address-form-section-title">Operational Hours</div>
                <Row gutter={8}>
                  <Col span={12}>
                    <Form.Item
                      colon={false}
                      label="Start Time"
                      className="custom-form operational-hours-time-item"
                    >
                      <MaterialTimePicker
                        style={{ width: "100%" }}
                        value={
                          location?.start_time
                            ? parseOperationalTime(location.start_time)
                            : null
                        }
                        onChange={(value) =>
                          handleOnChangeDirect(
                            "start_time",
                            normalizeOperationalTime(value)
                          )
                        }
                        isMilitaryTime={isMilitaryTime}
                        computeDisabledHours={() => []}
                        computeDisabledMinutes={() => []}
                        disabled={disableAddressFields || false}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      colon={false}
                      label="End Time"
                      className="custom-form operational-hours-time-item"
                    >
                      <MaterialTimePicker
                        style={{ width: "100%" }}
                        value={
                          location?.end_time
                            ? parseOperationalTime(location.end_time)
                            : null
                        }
                        onChange={(value) =>
                          handleOnChangeDirect(
                            "end_time",
                            normalizeOperationalTime(value)
                          )
                        }
                        isMilitaryTime={isMilitaryTime}
                        computeDisabledHours={() => []}
                        computeDisabledMinutes={() => []}
                        disabled={disableAddressFields || false}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Fragment>
            )}
          </Fragment>
        )}

      </Row>
    </Fragment>
    :
    <Fragment>
       <Row gutter={[0, 8]} className="address-form-v2" data-testid="address-form">

      <Row gutter={16}>
         {showSearch && (
        <Row style={{ padding: "6px 12px"}}>
          <Col span={24}>
            <Form.Item
              colon={false}
              label="Search For an Address"
              className="custom-form"
              key={`${location?.label_code}_search_address`}
            >
              <SearchAddress_v2
                onSelect={onSelectForCrossDock}
                hideWebResults={hideWebResults}
              />
            </Form.Item>
          </Col>
        </Row>
      )}

        {/* Company Section */}
        <Col span={24}>
          <Form.Item colon={false} label="Company" className="custom-form">
            <ReusableInput
              data-testid="company-name-input"
              value={location?.company_name}
              onChange={(e) =>
                handleOnChangeDirect("company_name", e.target.value)
              }
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        {/* Email Section */}
      <Col span={12} data-testid="customer-email">
          <Form.Item colon={false} label="Email" className="custom-form">
            <ReusableInput
              value={location?.customer_email}
              onChange={(e) =>
                handleOnChangeDirect("customer_email", e.target.value)
              }
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
        {/* Phone 1 Section */}
        <Col span={12} data-testid="customer-phone">
          <Form.Item colon={false} label="Phone" className="custom-form">
            <MobileInput_v2
              value={location?.customer_phone_one}
              handleInputChange={(val) =>
                handleOnChangeDirect("customer_phone_one", val)
              }
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
      </Row>
    </Row>

    </Fragment>
  );
};

const AddressForm_v2 = ({ location, onAddressSelect, handleOnChangeDirect,typeOfOrder, handleOnChangeAddress, form, getFieldDecorator }) => {
  const formRef = useRef(null);
  const RadioGroup = Radio.Group;
  const fullAddresss = _.compact(Object.values((_.pick(location || {}, ["customer_address_line_1", "customer_address_line_2" , "customer_city", "customer_state", "customer_country", "customer_zipcode"])))).join(',')
  return (
    <Fragment>
      {typeOfOrder === "MS" && !(location?.location_id) && (
      <Row>
      <Col sm={24} xs={24} md={16} lg={16} data-testid="location-type-select">
        <SideBySideFormItem
          label={"Location Type"}
        >
          <RadioGroup
            onChange={(e) => handleOnChangeDirect("type_of_loc", e.target.value)}
            value={location.type_of_loc}
          >
            <Radio value={"PICKUP"}>Pickup</Radio>
            <Radio value={"DELIVERY"}>Delivery</Radio>
          </RadioGroup>
        </SideBySideFormItem>
      </Col>
      </Row>
      )}
      <Row>
        <Col data-testid="search-address-dropdown">
          <SearchAddress_v2
            onSelect={onAddressSelect}
            doesAddressExist={true}
           // value={formatAddress(location)}
          />
        </Col>
      </Row>

      <Row className='marginTop8'>
        <Col className="table-col">
          <Form.Item
            label="Address"
            className="custom-form required-star-right"
            key={`${location?.label_code}_address`}
          >
            {/* <ReusableInput
              name="customer_address_line_1"
              value={location?.customer_address_line_1}
              onChange={(e) => handleOnChangeDirect("customer_address_line_1", e.target.value)}
              rules={[{ required: true, message: "Address is required" }]}
            /> */}
            <ReusableInput
              data-testid="address-input"
              name="full_address"
              value={fullAddresss}
              onChange={(e) => handleOnChangeAddress( e.target.value)}
              rules={[{ required: true, message: "Address is required" }]}
              disabled={disableAddressFields || false}
            />

          </Form.Item>
        </Col>
      </Row>
      <Row className='marginTop8'>
        <Col className="table-col">
          <Form.Item
            label="Location Code"
            className="custom-form"
            key={`${location?.label_code}_location_code`}
          >
            <ReusableInput
              data-testid="location-code-input"
              name="acc_loc_code"
              value={location?.acc_loc_code}
              onChange={(e) => handleOnChangeDirect("acc_loc_code", e.target.value)}
              rules={[{ required: true, message: "Address is required" }]}
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 0]} className="marginTop8">
        <Col span={12} className="table-col">
          <Form.Item label="Name" className="custom-form">
            <ReusableInput
              data-testid="customer-name-input"
              name="name"
              value={location?.customer_first_name}
              onChange={(e) => handleOnChangeDirect("customer_first_name", e.target.value)}
              label="Name"
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
        <Col span={12} className="table-col">
          <Form.Item label="Company" name="company_name">
            <ReusableInput
              data-testid="company-name-input"
              value={location?.company_name}
              onChange={(e) => handleOnChangeDirect("company_name", e.target.value)}
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 0]} className="marginTop8">
        <Col span={8} className="table-col-address">
          <Form.Item label="Address Type" name="address_type_address">
            <Select_v2
              data-testid="address-type-dropdown"
              labelField="label"
              valueField="value"
              value={location?.address_type}
              onChange={(value) => handleOnChangeDirect("address_type", value)}
              options={[
                { label: "Residential", value: "RESEDENTIAL", icon: <ResidentialIcon /> },
                { label: "Commercial", value: "COMMERCIAL", icon: <CommercialIcon /> },
              ]}
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
        <Col span={8} className="table-col">
          <Form.Item label="Operational Use" name="operational_use">
            <Select_v2
              labelField="label"
              valueField="value"
              value={location?.operational_use || "FINAL_MILE"}
              onChange={(value) => handleOnChangeDirect("operational_use", value)}
              options={[
                { label: "Cartage/White Glove", value: "FINAL_MILE" },
                { label: "Drayage", value: "DRAYAGE" },
                { label: "Both", value: "BOTH" },
              ]}
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
        <Col span={8} className="table-col">
          <Form.Item label="Email" name="customer_email">
            {getFieldDecorator("email", {
              rules: [
                {
                  type: "email",
                  message: "The email is not valid",
                },
                {
                  required: false,
                },
              ],
            })(
              <ReusableInput
                data-testid="customer-email-input"
                value={location?.customer_email}
                onChange={(e) => handleOnChangeDirect("customer_email", e.target.value)}
                disabled={disableAddressFields || false}
              />
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={[24, 0]} className="marginTop8">
        <Col span={12} className="table-col" data-testid="customer-phone-1-input">
          <Form.Item label="Phone 1" name="customer_phone_one">
            <MobileInput_v2
              value={location?.customer_phone_one}
              handleInputChange={(val) => handleOnChangeDirect("customer_phone_one", val)}
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
        <Col span={12} className="table-col" data-testid="customer-phone-2-input">
          <Form.Item label="Phone 2" name="customer_phone_two">
            <MobileInput_v2
              value={location?.customer_phone_two}
              handleInputChange={(val) => handleOnChangeDirect("customer_phone_two", val)}
              disabled={disableAddressFields || false}
            />
          </Form.Item>
        </Col>
      </Row>

    </Fragment>
  );
};

export default AddressForm_v2;

AddressForm_v2.propTypes = {
  typeOfOrder: PropTypes.string.isRequired,
  handleOnChangeAddress:  PropTypes.func,
};

AddressForm_v2.defaultProps = {
  typeOfOrder : "",
  handleOnChangeAddress: () => {},
};
